/**
 * Extrait un message d'erreur lisible depuis un objet d'erreur API
 * @param {Object} error - L'objet erreur retourné par l'API
 * @param {string} defaultMessage - Message par défaut si aucune erreur spécifique n'est trouvée
 * @returns {string} - Message d'erreur formaté
 */
export const extractErrorMessage = (error, defaultMessage = 'Une erreur est survenue') => {
    if (!error) return defaultMessage;
    
    // Si error.data contient les détails
    if (error.data) {
        // String direct
        if (typeof error.data === 'string') {
            return error.data;
        }
        
        // Detail object
        if (error.data.detail) {
            if (typeof error.data.detail === 'string') {
                return error.data.detail;
            }
            if (typeof error.data.detail === 'object') {
                const messages = Object.values(error.data.detail).flat();
                return messages.join(', ');
            }
        }
        
        // Message direct
        if (error.data.message) {
            return error.data.message;
        }
        
        // Erreurs de champs (format Django REST Framework)
        if (typeof error.data === 'object') {
            const errorMessages = [];
            Object.entries(error.data).forEach(([key, value]) => {
                if (key === 'non_field_errors') {
                    // Erreurs non liées à un champ spécifique
                    const msg = Array.isArray(value) ? value.join(', ') : value;
                    errorMessages.push(msg);
                } else {
                    // Erreurs liées à un champ
                    const msg = Array.isArray(value) ? value.join(', ') : value;
                    errorMessages.push(`${key}: ${msg}`);
                }
            });
            if (errorMessages.length > 0) {
                return errorMessages.join('; ');
            }
        }
    }
    
    // Fallback sur error.message
    if (error.message) {
        return error.message;
    }
    
    return defaultMessage;
};

/**
 * Extrait les erreurs de champs pour affichage dans un formulaire
 * @param {Object} error - L'objet erreur retourné par l'API
 * @returns {Object} - Objet avec les erreurs par champ
 */
export const extractFieldErrors = (error) => {
    const fieldErrors = {};
    
    if (!error || !error.data) return fieldErrors;
    
    if (error.data.errors) {
        return error.data.errors;
    }
    
    if (typeof error.data === 'object') {
        Object.entries(error.data).forEach(([key, value]) => {
            fieldErrors[key] = Array.isArray(value) ? value.join(', ') : value;
        });
    }
    
    return fieldErrors;
};

/**
 * Extrait un message d'erreur court pour les notifications
 * @param {Object} error - L'objet erreur retourné par l'API
 * @param {string} defaultMessage - Message par défaut
 * @returns {string} - Message d'erreur court
 */
export const extractShortErrorMessage = (error, defaultMessage = 'Une erreur est survenue') => {
    if (!error) return defaultMessage;
    
    // Essayer d'obtenir le premier message d'erreur
    if (error.data) {
        if (typeof error.data === 'string') {
            return error.data;
        }
        
        if (error.data.detail && typeof error.data.detail === 'string') {
            return error.data.detail;
        }
        
        if (error.data.message) {
            return error.data.message;
        }
        
        // Prendre la première erreur de champ
        if (typeof error.data === 'object') {
            const firstKey = Object.keys(error.data)[0];
            if (firstKey) {
                const value = error.data[firstKey];
                const msg = Array.isArray(value) ? value[0] : value;
                return firstKey === 'non_field_errors' ? msg : `${firstKey}: ${msg}`;
            }
        }
    }
    
    if (error.message) {
        return error.message;
    }
    
    return defaultMessage;
};
